import React, { useCallback, useEffect, useState } from 'react';
import {
  ENotificationEventType,
  IExportNotificationsSuccessResponse,
  INotification,
  INotificationSearchFilter,
  INotificationSearchRequest,
  TNotificationsSortField,
} from 'services/CrmApi/types/NotificationTypes';
import { NotificationItem } from './NotificationItem';
import { ESortOrder } from 'store/common/types';
import { useCrmNotifications } from 'services/CrmApi/queries/useCrmNotifications';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { NotificationsLoadMoreSection } from './NotificationsLoadMoreSection';
import { useDebounce } from 'hooks/useDebounce';
import { NotificationsCollectionWrapper } from './NotificationsCollectionWrapper';
import { useNotificationsExportMutation } from 'services/CrmApi/mutations/useNotificationsExport';
import { ISort } from 'services/BackendApi';
import { AxiosResponse } from 'axios';
import { useNotifications } from 'hooks/useNotifications';

const NOTIFICATIONS_PAGE_SIZE = 100;

interface INotificationsCollectionProps {
  startDate: string;
  endDate: string;
  isOpen: boolean;
}

export const NotificationsCollection: React.FC<INotificationsCollectionProps> = React.memo(
  ({ startDate, endDate, isOpen }) => {
    const { showSuccessNotification, showErrorNotification } = useNotifications();
    const [onlyShowUnread, setOnlyShowUnread] = useState(true);
    const handleOnlyShowUnreadChange = useCallback((onlyUnread: boolean) => {
      setOnlyShowUnread(onlyUnread);
    }, []);

    const [searchTerm, setSearchTerm, debouncedSearchTerm] = useDebounce('', 500);
    const handleSearchTermChange = useCallback((searchValue: string) => {
      setSearchTerm(searchValue);
    }, []);

    const [notificationType, setNotificationType] = useState<ENotificationEventType | undefined>(undefined);
    const handleNotificationTypeChange = useCallback((notificationType: ENotificationEventType) => {
      setNotificationType(notificationType);
    }, []);

    const filter: INotificationSearchFilter = {
      startDate,
      endDate,
      read: onlyShowUnread ? false : undefined,
      eventType: notificationType,
      directoryEntryFullName: debouncedSearchTerm ?? undefined,
    };

    const sort: ISort<TNotificationsSortField> = {
      key: 'date',
      order: ESortOrder.DESC,
    };

    // Get notifications from Backend API
    const notificationsRequest: INotificationSearchRequest = {
      filter,
      sort,
      pagination: { offset: 0, limit: NOTIFICATIONS_PAGE_SIZE },
    };
    const { notifications, crmNotificationsResponse, notificationsCount } = useCrmNotifications(
      notificationsRequest,
      isOpen
    );
    const [localNotifications, setLocalNotifications] = useState<INotification[]>(notifications);
    useEffect(() => {
      setLocalNotifications(notifications);
    }, [notifications.length, isOpen]);
    let filteredNotifications = [...localNotifications];
    if (onlyShowUnread) {
      filteredNotifications = localNotifications.filter(notification => notification.read === false);
    }
    const notifyReadStatus = useCallback((notificationId: string, read: boolean) => {
      setLocalNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId ? { ...notification, read } : notification
        )
      );
    }, []);

    // export
    const [isExportInProgress, setIsExportInProgress] = useState(false);
    const notificationsExportMutation = useNotificationsExportMutation();
    const handleExportNotifications = useCallback(async () => {
      try {
        setIsExportInProgress(true);
        const response = await notificationsExportMutation.mutateAsync({ filter, sort });
        if (response.status === 200 && response.data.signedUrl) {
          window.open(response.data.signedUrl, '_blank');
          showSuccessNotification('CSV exported successfully.');
        } else {
          showErrorNotification('Error exporting CSV');
        }
      } catch (error) {
        showErrorNotification('Error exporting CSV');
      } finally {
        setIsExportInProgress(false);
      }
    }, [filter, sort, notificationsExportMutation, showSuccessNotification, showErrorNotification]);

    // Load more button
    const handleLoadMore = useCallback(() => {
      if (crmNotificationsResponse.hasNextPage) {
        crmNotificationsResponse.fetchNextPage();
      }
    }, [crmNotificationsResponse]);
    const [isUpdating, setIsUpdating] = useState(false);
    const notifyUpdateStatus = useCallback((isUpdating: boolean) => {
      setIsUpdating(isUpdating);
    }, []);
    const shouldShowLoadMoreSection = !crmNotificationsResponse.isFetching && !isUpdating;

    if (crmNotificationsResponse.isError) {
      return (
        <NotificationsCollectionWrapper>
          <ErrorBar message="Failed to load notifications" className="mb-[20px]" />
        </NotificationsCollectionWrapper>
      );
    }

    // Show loading bar if notifications are still loading and no cached notifications are available
    if (crmNotificationsResponse.isLoading && filteredNotifications.length === 0) {
      return (
        <NotificationsCollectionWrapper
          notifications={[]}
          onlyShowUnread={onlyShowUnread}
          onOnlyShowUnreadChange={handleOnlyShowUnreadChange}
          isLoading
        >
          <div className="mt-[20px] mb-[35px]">
            <LoadingBar label="Loading notifications..." />
          </div>
        </NotificationsCollectionWrapper>
      );
    }

    // Show empty state if notifications are fetched and no cached notifications are available
    if (crmNotificationsResponse.isFetched && filteredNotifications.length === 0) {
      return (
        <NotificationsCollectionWrapper
          notifications={[]}
          onlyShowUnread={onlyShowUnread}
          onOnlyShowUnreadChange={handleOnlyShowUnreadChange}
          searchValue={searchTerm}
          onSearchValueChange={handleSearchTermChange}
        >
          <p className="font-pt-sans text-15px text-black capitalize italic my-[20px]">
            No New notifications from the last 30 days.
          </p>
        </NotificationsCollectionWrapper>
      );
    }

    return (
      <NotificationsCollectionWrapper
        notifications={isOpen ? filteredNotifications : undefined}
        onlyShowUnread={onlyShowUnread}
        onOnlyShowUnreadChange={handleOnlyShowUnreadChange}
        searchValue={searchTerm}
        onSearchValueChange={handleSearchTermChange}
        notificationType={notificationType}
        onNotificationTypeChange={handleNotificationTypeChange}
        onNotificationsExport={handleExportNotifications}
        isExportInProgress={isExportInProgress}
      >
        {isOpen ? (
          <div className="notifications-collection flex flex-col gap-[10px] overflow-y-auto pb-[20px]">
            {filteredNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                notifyReadStatus={notifyReadStatus}
                notifyUpdateStatus={notifyUpdateStatus}
              />
            ))}
            {shouldShowLoadMoreSection ? (
              <NotificationsLoadMoreSection
                notificationsCount={notificationsCount}
                notifications={filteredNotifications}
                onLoadMoreClick={handleLoadMore}
                isFetchingNextPage={crmNotificationsResponse.isFetchingNextPage}
              />
            ) : null}
          </div>
        ) : null}
      </NotificationsCollectionWrapper>
    );
  }
);
